/* global jQuery */

import React, { Component } from "react";
import PropTypes from "prop-types";
import debounce from "just-debounce";
import deepEqual from "deep-equal";
import classnames from "classnames";

import _str from "shared/util/_str";
import OkInput from "shared/components/oknf/OkInput";
import Throbber from "shared/components/Throbber";
import Results from "shared/components/SearchWithSuggestionsResults";
import domNodesToArray from "shared/util/domNodesToArray";

import "./SearchWithSuggestions.scss";

class SearchWithSuggestions extends Component {
	state = {
		results: null,
	}

	componentDidMount() {
		// throttle location searches
		this.executeSearch = debounce(this.props.searchFunction, 500);

		document.addEventListener("click", this._handleBodyClick);
		document.addEventListener("keydown", this._handleKeydown);

		// if we passed a search in as props, trigger a change event
		if (this.props.search && this.props.search !== "") {
			const e = {};
			e.target = {};
			e.target.value = this.props.search;

			this._handleChange(e, this.props.suppressAutomaticSearch);
		}
	}

	componentWillReceiveProps(nextProps) {
		this.setState({ isSearching: false });

		if (!deepEqual(this.props.results, nextProps.results)) {
			this.setState({
				results: nextProps.results,
				activeResult: null,
			});

			this.props.onResultsChange(nextProps.results);
		}

		if (this.props.search !== nextProps.search) {
			this.setState({ search: nextProps.search });
		}
	}

	componentWillUnmount() {
		document.removeEventListener("click", this._handleBodyClick);
		document.removeEventListener("keydown", this._handleKeydown);
	}

	_handleChange = (e, suppressAutomaticSearch = false) => {
		const val = e.target.value;
		const isSearching = val !== "" && val.length >= 3;

		const update = {
			search: val,
			isSearching: isSearching,
		};

		if (this.props.onTextInput) {
			this.props.onTextInput(e);
		}

		this.setState(update);

		if (suppressAutomaticSearch) { return; }

		if (val !== "") {
			if (!isSearching) {
				return;
			}

			this.executeSearch(val);
		} else {
			this._killResults();
		}
	}

	// keyboard nav in the search results dropdown
	_handleKeydown = (e) => {
		if (e.target.getAttribute("name") !== "search-and-select-input") {
			return;
		}

		// short circuit if there are no results and we are only allowing tokenized
		// searches
		if (
			!this.props.allowArbitrarySearch &&
			(!this.state.results || !this.state.results.length)
		) {
			return;
		}

		let newResult;

		switch (e.key) {
			case "ArrowDown": {
				e.preventDefault();

				if (this.state.activeResult === null) {
					newResult = 0;
				} else {
					newResult = this.state.activeResult + 1;
				}

				break;
			}

			case "ArrowUp": {
				e.preventDefault();

				if (this.state.activeResult === null) {
					newResult = this.state.results.length - 1;
				} else {
					newResult = this.state.activeResult - 1;
				}

				break;
			}

			case "Enter": {
				e.preventDefault();

				if (!this.state.activeResult &&
					this.props.allowArbitrarySearch &&
					e.target &&
					e.target.value) {
					this._handleResultClick({ text: e.target.value });
				} else if (this.state.activeResult !== null) {
					this._handleResultClick(this.state.results[this.state.activeResult]);
					jQuery("input[name='search-and-select-input']").blur();
					newResult = null;
				}

				return;
			}

			case "Escape": {
				this._killResults();
				return;
			}

			default: {
				return;
			}
		}

		// keep in bounds
		if (!this.state.results) {
			newResult = null;
		} else if (newResult < 0) {
			newResult = 0;
		} else if (newResult >= this.state.results.length) {
			newResult = this.state.results.length - 1;
		}

		this.setState({ activeResult: newResult });
	}

	_handleClearClick = () => {
		this._killResults();
	}

	// on body click, dispell the filter if it's open
	_handleBodyClick = (e) => {
		// short circuit if the results are null
		if (this.state.results === null) {
			return;
		}

		// short circuit if you're clicking within the search input. use jQuery here
		// because Firefox doesn't support event.path to get a list of parent nodes
		const searchWrapper = this.searchWrapper;
		const parents = jQuery(e.target).parents();

		domNodesToArray(parents).forEach((parent) => {
			if (parent === searchWrapper) {
				return;
			}
		});

		// short circuit if you're clicking inside the dropdown
		const dropdown = this.searchAndSelectResults;

		if (e.path) {
			e.path.forEach((node) => {
				if (node === dropdown) {
					return;
				}
			});
		}

		// if we made it this far, close the dropdown
		this._killResults();
	}

	// choose a result, slugger
	_handleResultClick = (result) => {
		this.props.onResultClick(result);
		this._killResults();
	}

	_killResults = () => {
		const newState = {
			isSearching: false,
			search: "",
			results: null,
			activeResult: null,
		};

		// if you can search for anything, there's no
		// need to clear the box
		if (this.props.allowArbitrarySearch) {
			delete newState.search;
		}

		this.setState(newState);

		this.props.onResultsChange(null);
	}

	render() {
		let results;
		let clearClasses;
		let clearButton;
		let errorMessage;

		if (this.state.results !== null) {
			results = (
				<Results
					search={this.state.search}
					results={this.state.results}
					activeResult={this.state.activeResult}
					resultClick={this._handleResultClick}
					ref={(el) => { this.searchAndSelectResults = el; }}
				/>
			);

			clearClasses = classnames({
				"clear-search": true,
				"is-searching": this.state.isSearching,
			});

			clearButton = (
				<button
					className={clearClasses}
					onClick={this._handleClearClick}
				>
					<span className="reader-text">{_str("clear search")}</span>
				</button>
			);
		}

		const inputClasses = classnames({
			"has-results": this.state.results,
		});

		const wrapperClasses = classnames({
			"input-wrapper": true,
			"has-results": this.state.results,
			"disabled": this.props.searchProps && this.props.searchProps.disabled,
			"is-searching": this.state.isSearching,
		});

		if (this.state.error) {
			errorMessage = (
				<div className="search-and-select-error">
					{_str("Oh snap, something went wrong. Try again later!")}
				</div>
			);
		}

		return (
			<div className="search-and-select">
				<div className={wrapperClasses} ref={(el) => { this.searchWrapper = el; }}>
					<OkInput
						type="search"
						name="search-and-select-input"
						className={inputClasses}
						{...this.props.searchProps}
						onChange={this._handleChange}
						onFocus={this.props.onFocus}
						value={this.state.search || ""}
						maxLength={this.props.maxLength}
					/>
					<Throbber show={this.state.isSearching} />
					{results}
					{clearButton}
				</div>
				{errorMessage}
			</div>
		);
	}
}

SearchWithSuggestions.propTypes = {
	results: PropTypes.array,
	onFocus: PropTypes.func,
	onTextInput: PropTypes.func,
	onResultClick: PropTypes.func.isRequired,
	onResultsChange: PropTypes.func.isRequired,
	searchProps: PropTypes.object,
	searchFunction: PropTypes.func.isRequired,
	search: PropTypes.string,
	allowArbitrarySearch: PropTypes.bool,
	suppressAutomaticSearch: PropTypes.bool,
	maxLength: PropTypes.number,
};

SearchWithSuggestions.defaultProps = {
	allowArbitrarySearch: false,
	suppressAutomaticSearch: false,
	maxLength: null,
};

export default SearchWithSuggestions;



// WEBPACK FOOTER //
// ./src/shared/components/SearchWithSuggestions.jsx